
import React, { useState, useEffect } from 'react';
import { Language, translations } from '../translations';
import { User, FlightProfile, SiteAssets } from '../types';
import { ApiService } from '../services/api';
import QuickTipsGallery from './QuickTipsGallery';
import { BarChart3, Bot, Coins, FolderOpen, Gem, Lock, Mail, Map, MapPin, ShieldAlert, ShoppingBag, User as UserIcon, Users, Camera, Sparkles, Lightbulb } from 'lucide-react';

interface HeaderProps {
  activeTab: string;
  onTabChange: (tab: any) => void;
  lang: Language;
  setLang: (lang: Language) => void;
  user: User | null;
  profile: FlightProfile;
  onLogout: () => void;
  testModeName?: string | null;
  unreadMsgCount?: number;
  friendRequestCount?: number;
  unreadSharedCount?: number;
  onShowLanding?: () => void;
  profileIncomplete?: boolean;
  onOpenFeatureList?: () => void;
}

const Header: React.FC<HeaderProps> = ({ activeTab, onTabChange, lang, setLang, user, profile, onLogout, testModeName, unreadMsgCount = 0, friendRequestCount = 0, unreadSharedCount = 0, onShowLanding, onOpenFeatureList }) => {
  const t = translations[lang];
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';
  const [logoUrl, setLogoUrl] = useState('https://skstreckenfliegen.blob.core.windows.net/mediadaten/logo-small.png');
  const [headerTitle, setHeaderTitle] = useState<string | undefined>(undefined);
  const [headerSubtitle, setHeaderSubtitle] = useState<string | undefined>(undefined);
  const [showHelp, setShowHelp] = useState(false);

  const logoInputRef = React.useRef<HTMLInputElement>(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  const handleInlineLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      try {
          setUploadingLogo(true);
          const reader = new FileReader();
          reader.onload = async (e) => {
              try {
                  const data = e.target?.result as string;
                  const res = await ApiService.uploadFile(file.name, file.type, data);
                  
                  // Fetch current settings
                  const settings = await ApiService.getSystemSettings();
                  const currentAssets = settings && settings['site_assets'] ? JSON.parse(settings['site_assets']) : {};
                  
                  // Update with new logo, clear title to allow full width display if preferred
                  const newAssets = { ...currentAssets, logoUrl: res.blob_url, headerTitle: '' };
                  
                  // Save
                  await ApiService.saveSystemSetting('site_assets', JSON.stringify(newAssets));
                  
                  // Dispatch to all components
                  window.dispatchEvent(new CustomEvent('site-assets-update', { detail: newAssets }));
              } catch (err: any) {
                  console.error("Failed to inline upload logo", err);
                  alert("Fehler beim Logo Upload: " + String(err.message || Object.values(err).join(", ") || "Unbekannter Fehler"));
              } finally {
                  setUploadingLogo(false);
              }
          };
          reader.readAsDataURL(file);
      } catch(err) {
          console.error(err);
          setUploadingLogo(false);
      }
  };

  // Dynamic Logo Loading
  useEffect(() => {
    const loadAssets = async () => {
      try {
        const settings = await ApiService.getSystemSettings();
        if (settings && settings['site_assets']) {
          const assets = JSON.parse(settings['site_assets']) as SiteAssets;
          if (assets.logoUrl) setLogoUrl(assets.logoUrl);
          if (assets.headerTitle !== undefined) setHeaderTitle(assets.headerTitle);
          if (assets.headerSubtitle !== undefined) setHeaderSubtitle(assets.headerSubtitle);
          localStorage.setItem('xc_site_assets', settings['site_assets']); // Cache for Loading Screen
        }
      } catch (e) {
        // Silent fail to default
      }
    };

    const handleUpdate = (e: any) => {
      const assets = e.detail as SiteAssets;
      if (assets && assets.logoUrl) setLogoUrl(assets.logoUrl);
      if (assets && assets.headerTitle !== undefined) setHeaderTitle(assets.headerTitle);
      if (assets && assets.headerSubtitle !== undefined) setHeaderSubtitle(assets.headerSubtitle);
      if (assets) localStorage.setItem('xc_site_assets', JSON.stringify(assets));
    };

    window.addEventListener('site-assets-update', handleUpdate);
    loadAssets(); // Initial load

    return () => window.removeEventListener('site-assets-update', handleUpdate);
  }, []);

  // Check if profile is incomplete (Robust Check matching ProfileManager)
  const stats = profile?.stats;
  const isProfileIncomplete = !stats ||
    !stats.fitness ||
    (!stats.disciplines || stats.disciplines.length === 0) ||
    !stats.groundHandling ||
    (!profile.gliders || profile.gliders.length === 0) ||
    (!profile.harnesses || profile.harnesses.length === 0);

  return (
    <header className="bg-slate-950/80 backdrop-blur-md border-b border-white/5 py-2.5 sm:py-4 z-[2000]">
      <div className="container mx-auto px-3 sm:px-6 flex items-center justify-between">
        <div className="flex items-center gap-2.5 sm:gap-3 cursor-pointer group relative ml-[2px] sm:ml-[5px] shrink min-w-0" onClick={(e) => {
            // If admin and they click the edit button, don't change tab
            if ((e.target as HTMLElement).closest('.edit-logo-btn')) {
                e.stopPropagation();
                return;
            }
            onTabChange('map');
        }}>
          {isAdmin && (
              <label 
                  className="edit-logo-btn absolute -inset-2 bg-black/60 rounded-xl flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-[2000] cursor-pointer"
                  onClick={(e) => e.stopPropagation()}
              >
                  <input type="file" accept="image/png, image/jpeg, image/svg+xml, image/webp" className="hidden" onChange={handleInlineLogoUpload} />
                  {uploadingLogo ? (
                      <span className="text-white text-xs font-bold animate-pulse">⏳ Lade...</span>
                  ) : (
                      <span className="text-sky-400 text-xs font-bold flex items-center gap-1 bg-slate-900/80 px-2 py-1 rounded shadow pointer-events-none"><span className="text-[10px]">✏️</span> Logo Upload</span>
                  )}
              </label>
          )}
          <div className={`${headerTitle === '' ? 'h-10 sm:h-14 pb-1 min-w-[40px] sm:min-w-[56px] items-center justify-center overflow-hidden shrink-0' : 'w-9 h-9 sm:w-10 sm:h-10 rounded-xl flex items-center justify-center shadow-xl group-hover:scale-110 transition-transform overflow-hidden bg-slate-900 border border-white/10 shrink-0'}`}>
            {headerTitle === '' ? (
                <img src={logoUrl} alt="Streckenfliegen.app Logo" className="h-full w-auto object-contain" />
            ) : (
                <img src={logoUrl} alt="Streckenfliegen.app Logo" className="w-full h-full object-contain" />
            )}
          </div>
          {headerTitle !== '' && (
              <div className="max-w-[130px] xs:max-w-[170px] sm:max-w-none min-w-0">
                <h1 className="text-base sm:text-xl font-black text-white tracking-tighter leading-none truncate">{headerTitle ?? t.title}</h1>
                <span className="text-[8.5px] sm:text-[10px] font-bold text-slate-400 uppercase tracking-[0.05em] sm:tracking-[0.08em] block truncate">
                  {headerSubtitle !== undefined ? headerSubtitle : (
                      <>Plane <span className="text-sky-400">Smarter</span> · Fliege <span className="text-emerald-400">Weiter</span></>
                  )}
                </span>
              </div>
          )}
        </div>

        {/* Test Mode Banner */}
        {testModeName && (
          <div className="absolute left-1/2 -translate-x-1/2 top-4 bg-amber-500 text-black px-4 py-1.5 rounded-full font-black text-xs uppercase tracking-widest animate-pulse shadow-[0_0_20px_rgba(245,158,11,0.6)] z-[3000]">
            TEST: {testModeName}
          </div>
        )}

        {/* Main Navigation Block */}
        <nav className="hidden lg:flex gap-3 bg-slate-900 p-1.5 rounded-xl border border-white/5 items-stretch ml-auto shadow-2xl">
          
          <div className="flex flex-col gap-1.5 justify-center">
            {/* Upper Row: Core Flight Features */}
            <div className="flex flex-wrap items-center justify-end gap-1">
              <button
                onClick={() => onOpenFeatureList ? onOpenFeatureList() : window.dispatchEvent(new CustomEvent('open-feature-list'))}
                className="h-8 px-2.5 rounded-lg text-xs font-black uppercase tracking-wider transition-all border border-sky-500/30 bg-sky-500/10 hover:bg-sky-500/20 text-sky-300 flex items-center justify-center whitespace-nowrap shadow-sm shadow-sky-950/40 relative group mr-1"
                title="Funktionsliste & Neuerungen der Plattform (chronologisch)"
              >
                <Sparkles className="w-3.5 h-3.5 mr-1 text-amber-400 animate-pulse" />
                Funktionen
                <span className="ml-1.5 px-1.5 py-0.5 rounded text-[8.5px] font-black bg-emerald-500 text-slate-950 uppercase tracking-tight shadow">
                  Neu
                </span>
              </button>
              <button
                onClick={() => onTabChange('map')}
                className={`h-8 px-2.5 rounded-lg text-xs font-bold uppercase tracking-wider transition-all border flex items-center justify-center whitespace-nowrap ${activeTab === 'map' ? 'bg-sky-500/10 text-sky-400 border-sky-500/20 shadow-lg shadow-sky-900/20' : 'bg-transparent text-slate-500 border-transparent hover:text-slate-300 hover:bg-white/5'}`}
              >
                <Map className="w-3.5 h-3.5 mr-1.5" />
                {t.nav.terrain}
              </button>
              <button
                onClick={() => onTabChange('planner')}
                className={`h-8 px-2.5 rounded-lg text-xs font-bold uppercase tracking-wider transition-all border flex items-center justify-center whitespace-nowrap ${activeTab === 'planner' ? 'bg-sky-500/10 text-sky-400 border-sky-500/20 shadow-lg shadow-sky-900/20' : 'bg-transparent text-slate-500 border-transparent hover:text-slate-300 hover:bg-white/5'} ${user?.permissions && !user.permissions.track_planning ? 'opacity-30 cursor-not-allowed pointer-events-none grayscale' : ''} ${isProfileIncomplete ? 'opacity-50 grayscale' : ''}`}
              >
                {isProfileIncomplete ? <Lock className="w-3.5 h-3.5 mr-1.5" /> : <MapPin className="w-3.5 h-3.5 mr-1.5" />}
                {t.nav.analyst}
              </button>
              <button
                onClick={() => onTabChange('library')}
                className={`h-8 px-2.5 rounded-lg text-xs font-bold uppercase tracking-wider transition-all border flex items-center justify-center relative whitespace-nowrap ${activeTab === 'library' ? 'bg-sky-500/10 text-sky-400 border-sky-500/20 shadow-lg shadow-sky-900/20' : 'bg-transparent text-slate-500 border-transparent hover:text-slate-300 hover:bg-white/5'} ${isProfileIncomplete ? 'opacity-50 grayscale' : ''}`}
              >
                {isProfileIncomplete ? <Lock className="w-3.5 h-3.5 mr-1.5" /> : <FolderOpen className="w-3.5 h-3.5 mr-1.5" />}
                {t.nav.library}
                {unreadSharedCount > 0 && !isProfileIncomplete && (
                  <span className="absolute -top-1 -right-1 bg-red-600 text-white text-[9px] min-w-[16px] h-4 rounded-full flex items-center justify-center px-1 shadow-sm animate-pulse">
                    {unreadSharedCount}
                  </span>
                )}
              </button>
              <button
                onClick={() => onTabChange('chat')}
                className={`h-8 px-2.5 rounded-lg text-xs font-bold uppercase tracking-wider transition-all border flex items-center justify-center whitespace-nowrap ${activeTab === 'chat' ? 'bg-sky-500/10 text-sky-400 border-sky-500/20 shadow-lg shadow-sky-900/20' : 'bg-transparent text-slate-500 border-transparent hover:text-slate-300 hover:bg-white/5'} ${(!user?.subscription_plan_id && !['admin', 'superadmin', 'vip'].includes(user?.role || '')) || isProfileIncomplete ? 'ring-2 ring-amber-400/60 border-amber-400/40 opacity-60 grayscale' : ''}`}
                title={!user?.subscription_plan_id && !['admin', 'superadmin', 'vip'].includes(user?.role || '') ? 'Für diese Funktion wird ein Abo benötigt' : undefined}
              >
                {isProfileIncomplete ? <Lock className="w-3.5 h-3.5 mr-1.5" /> : <Bot className="w-3.5 h-3.5 mr-1.5" />}
                {t.nav.advisor}
              </button>
            </div>

            {/* Lower Row: Social, Profile & Admin */}
            <div className="flex flex-wrap items-center justify-end gap-1">
              <button
                onClick={() => onTabChange('buddies')}
                className={`h-8 px-2.5 rounded-lg text-xs font-bold uppercase tracking-wider transition-all border flex items-center justify-center relative whitespace-nowrap ${activeTab === 'buddies' ? 'bg-sky-500/10 text-sky-400 border-sky-500/20 shadow-lg shadow-sky-900/20' : 'bg-transparent text-slate-500 border-transparent hover:text-slate-300 hover:bg-white/5'} ${isProfileIncomplete ? 'opacity-50 grayscale' : ''}`}
              >
                {isProfileIncomplete ? <Lock className="w-3.5 h-3.5 mr-1.5" /> : <Users className="w-3.5 h-3.5 mr-1.5" />}
                BUDDIES
                {friendRequestCount > 0 && !isProfileIncomplete && (
                  <span className="absolute -top-1 -right-1 bg-red-600 text-white text-[9px] min-w-[16px] h-4 rounded-full flex items-center justify-center px-1 shadow-sm animate-pulse">
                    {friendRequestCount}
                  </span>
                )}
              </button>
              <button
                onClick={(e) => { if (user?.permissions && !user.permissions.messaging) { e.preventDefault(); return; } onTabChange('messages'); }}
                className={`h-8 px-2.5 rounded-lg text-xs font-bold uppercase tracking-wider transition-all border flex items-center justify-center relative whitespace-nowrap ${activeTab === 'messages' ? 'bg-sky-500/10 text-sky-400 border-sky-500/20 shadow-lg shadow-sky-900/20' : 'bg-transparent text-slate-500 border-transparent hover:text-slate-300 hover:bg-white/5'} ${user?.permissions && !user.permissions.messaging ? 'opacity-30 cursor-not-allowed pointer-events-none grayscale' : ''} ${isProfileIncomplete ? 'opacity-50 grayscale' : ''}`}
              >
                {isProfileIncomplete ? <Lock className="w-3.5 h-3.5 mr-1.5" /> : <Mail className="w-3.5 h-3.5 mr-1.5" />}
                Nachrichten
                {unreadMsgCount > 0 && !isProfileIncomplete && (
                  <span className="absolute -top-1 -right-1 bg-red-600 text-white text-[9px] min-w-[16px] h-4 rounded-full flex items-center justify-center px-1 shadow-sm animate-pulse">
                    {unreadMsgCount}
                  </span>
                )}
              </button>
              <button
                onClick={() => onTabChange('market')}
                className={`h-8 px-2.5 rounded-lg text-xs font-bold uppercase tracking-wider transition-all border flex items-center justify-center relative whitespace-nowrap ${activeTab === 'market' ? 'bg-sky-500/10 text-sky-400 border-sky-500/20 shadow-lg shadow-sky-900/20' : 'bg-transparent text-slate-500 border-transparent hover:text-slate-300 hover:bg-white/5'}`}
              >
                <ShoppingBag className="w-3.5 h-3.5 mr-1.5" />
                MARKT
              </button>
              <button
                onClick={(e) => { if (user?.permissions && !user.permissions.statistics) { e.preventDefault(); return; } onTabChange('statistics'); }}
                className={`h-8 px-2.5 rounded-lg text-xs font-bold uppercase tracking-wider transition-all border flex items-center justify-center relative whitespace-nowrap ${activeTab === 'statistics' ? 'bg-purple-500/10 text-purple-400 border-purple-500/20 shadow-lg shadow-purple-900/20' : 'bg-transparent text-slate-500 border-transparent hover:text-slate-300 hover:bg-white/5'} ${user?.permissions && !user.permissions.statistics ? 'opacity-30 cursor-not-allowed pointer-events-none grayscale' : ''} ${isProfileIncomplete ? 'opacity-50 grayscale' : ''}`}
              >
                {isProfileIncomplete ? <Lock className="w-3.5 h-3.5 mr-1.5" /> : <BarChart3 className="w-3.5 h-3.5 mr-1.5" />}
                STATISTIK
              </button>
              <button
                onClick={(e) => { if (user?.permissions && !user.permissions.profile_management) { e.preventDefault(); return; } onTabChange('profile'); }}
                className={`h-8 px-2.5 rounded-lg text-xs font-bold uppercase tracking-wider transition-all border flex items-center justify-center relative whitespace-nowrap ${activeTab === 'profile' ? 'bg-sky-500/10 text-sky-400 border-sky-500/20 shadow-lg shadow-sky-900/20' : 'bg-transparent text-slate-500 border-transparent hover:text-slate-300 hover:bg-white/5'} ${user?.permissions && !user.permissions.profile_management ? 'opacity-30 cursor-not-allowed pointer-events-none grayscale' : ''}`}
              >
                <UserIcon className="w-3.5 h-3.5 mr-1.5" />
                {t.nav.profile}
                {isProfileIncomplete && (
                  <span className="absolute -top-1 -right-1 flex h-3 w-3">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
                  </span>
                )}
              </button>
              {isAdmin && (
                <>
                  <button
                    onClick={() => window.dispatchEvent(new CustomEvent('global-snip-start'))}
                    className="h-8 w-8 rounded-lg text-slate-400 hover:text-white hover:bg-white/5 border border-transparent transition-all flex items-center justify-center whitespace-nowrap mr-1"
                    title="Bildschirmausschnitt fotografieren (für News oder Social Media Mediathek)"
                  >
                    <Camera className="w-4 h-4 text-emerald-400" />
                  </button>
                  <button
                    onClick={() => onTabChange('admin')}
                    className={`h-8 px-2.5 rounded-lg text-xs font-bold uppercase tracking-wider transition-all border flex items-center justify-center whitespace-nowrap ${activeTab === 'admin' ? 'bg-amber-500/10 text-amber-500 border-amber-500/20 shadow-lg shadow-amber-900/20' : 'text-amber-500/70 border-transparent hover:text-amber-400 hover:bg-amber-400/10'}`}
                  >
                    <ShieldAlert className="w-3.5 h-3.5 mr-1.5" />
                    ADMIN
                  </button>
                </>
              )}
            </div>
          </div>

          {user && (
            <>
              <div className="hidden lg:block w-[1px] bg-white/10 mx-1"></div>
              <div className="hidden md:flex flex-col justify-between py-0.5 pr-2 pl-1 min-w-[140px]">
                <div className="flex items-center justify-end gap-3 w-full">
                  <div className="text-right">
                    <div className="text-[9px] font-black text-slate-500 uppercase tracking-widest leading-none mb-0.5">User</div>
                    <div className="text-[11px] font-bold text-sky-400 truncate max-w-[120px] leading-none">
                      {(user.email && typeof user.email === 'string') ? user.email.split('@')[0] : (user.full_name || 'Pilot')}
                    </div>
                  </div>
                  {onShowLanding && (
                    <button
                      onClick={onShowLanding}
                      className="h-7 px-2 rounded-lg bg-sky-600/20 text-sky-400 font-bold text-[10px] uppercase tracking-wider flex items-center justify-center border border-sky-500/20 hover:bg-sky-500/30 hover:text-white transition-all flex-shrink-0"
                      title="Zur Startseite"
                    >
                      Startseite
                    </button>
                  )}
                  <button
                    onClick={onLogout}
                    className="w-7 h-7 rounded-lg bg-slate-800 flex items-center justify-center border border-white/5 text-slate-300 hover:bg-red-950/50 hover:text-red-400 transition-all hover:border-red-900/50 flex-shrink-0"
                    title="Logout"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
                      <polyline points="10 17 15 12 10 7" />
                      <line x1="15" x2="3" y1="12" y2="12" />
                    </svg>
                  </button>
                </div>

                {/* Token Balance or Upgrade Button */}
                <div className="flex justify-end w-full mt-auto">
                    {(() => {
                      const planName = user.subscription?.plan || (user as any).subscription_plan_name || '';
                      const isFreeOrInvalid = 
                        !user.subscription_plan_id || 
                        planName.toLowerCase() === 'free' || 
                        user.subscription?.status === 'cancelled';
                      
                      const totalTokens = Math.max(0, (user.tokens_subscription || 0) + (user.tokens_purchased || 0));

                      if (isFreeOrInvalid && totalTokens === 0) {
                        return (
                          <button 
                            onClick={() => window.dispatchEvent(new CustomEvent('open-pricing', { detail: { tab: 'plans' } }))} 
                            className="flex items-center gap-1 px-1.5 py-0.5 bg-emerald-600/90 hover:bg-emerald-500 text-white font-bold text-[9px] rounded border border-emerald-400 shadow-[0_0_10px_rgba(16,185,129,0.2)] transition-colors whitespace-nowrap uppercase tracking-wider h-[20px]"
                          >
                            <span className="text-[10px]"><Gem className="w-4 h-4 inline mr-1 text-sky-500" /></span> PRO Abo kaufen
                          </button>
                        );
                      } else {
                        return (
                          <div className="flex items-center gap-2">
                            {isFreeOrInvalid && totalTokens > 0 && (
                              <button 
                                onClick={() => window.dispatchEvent(new CustomEvent('open-pricing', { detail: { tab: 'plans' } }))} 
                                className="hidden md:flex items-center gap-1 px-1.5 py-0.5 bg-emerald-600/50 hover:bg-emerald-500 text-white font-bold text-[8px] rounded border border-emerald-400/50 transition-colors uppercase tracking-wider h-[20px]"
                              >
                                <Gem className="w-4 h-4 inline mr-1 text-sky-500" /> PRO Abo
                              </button>
                            )}
                            <div className="flex items-center h-[20px]">
                              <div className="flex items-center h-full gap-1 px-1.5 rounded-l border border-amber-500/30 border-r-0 bg-amber-500/10">
                                <span className="text-[10px] tabular-nums leading-none"><Coins className="w-4 h-4 inline mr-1" /></span>
                                <span className="text-[10px] font-black text-amber-400 tabular-nums leading-none mt-[1px]">
                                  {totalTokens.toLocaleString('de-CH')}
                                </span>
                              </div>
                              <button 
                                onClick={() => window.dispatchEvent(new CustomEvent('open-pricing', { detail: { tab: 'tokens' } }))} 
                                className="px-1.5 h-full bg-amber-500 hover:bg-amber-400 text-amber-950 font-black text-[10px] rounded-r border border-amber-500 transition-colors shadow-[0_0_10px_rgba(245,158,11,0.2)] flex items-center justify-center leading-none" 
                                title="Tokens nachladen"
                              >
                                +
                              </button>
                            </div>
                          </div>
                        );
                      }
                    })()}
                  </div>
              </div>
            </>
          )}
        </nav>

        {/* Mobile Header Controls */}
        <div className="flex lg:hidden items-center gap-2 shrink-0 ml-auto">
          {user && (() => {
            const planName = user.subscription?.plan?.name || '';
            const isFreeOrInvalid = 
              !user.subscription || 
              user.subscription?.status === 'inactive' || 
              !user.subscription_plan_id || 
              planName.toLowerCase() === 'free' || 
              user.subscription?.status === 'cancelled';
            
            const totalTokens = Math.max(0, (user.tokens_subscription || 0) + (user.tokens_purchased || 0));

            return (
              <div className="flex items-center gap-1.5">
                {/* PRO or Tokens */}
                {isFreeOrInvalid && totalTokens === 0 ? (
                  <button 
                    onClick={() => window.dispatchEvent(new CustomEvent('open-pricing', { detail: { tab: 'plans' } }))} 
                    className="flex items-center gap-1 px-2 py-1 bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-[10px] rounded-lg border border-emerald-400 shadow-[0_0_10px_rgba(16,185,129,0.2)] transition-colors whitespace-nowrap uppercase tracking-wider h-[28px]"
                  >
                    <Gem className="w-3 h-3 text-sky-300" />
                    <span>PRO</span>
                  </button>
                ) : (
                  <div className="flex items-center h-[28px]">
                    <button 
                      onClick={() => window.dispatchEvent(new CustomEvent('open-pricing', { detail: { tab: 'tokens' } }))}
                      className="flex items-center h-full gap-1 px-2 rounded-l-lg border border-amber-500/30 border-r-0 bg-amber-500/10 active:bg-amber-500/20"
                      title="Tokens anzeigen"
                    >
                      <Coins className="w-3 h-3 text-amber-400" />
                      <span className="text-[10px] font-black text-amber-400 tabular-nums">
                        {totalTokens.toLocaleString('de-CH')}
                      </span>
                    </button>
                    <button 
                      onClick={() => window.dispatchEvent(new CustomEvent('open-pricing', { detail: { tab: 'tokens' } }))} 
                      className="px-2 h-full bg-amber-500 hover:bg-amber-400 text-amber-950 font-black text-xs rounded-r-lg border border-amber-500 transition-colors shadow-sm flex items-center justify-center leading-none" 
                      title="Tokens nachladen"
                    >
                      +
                    </button>
                  </div>
                )}

                {/* Quick Tips */}
                <button
                  onClick={() => setShowHelp(true)}
                  className="w-[28px] h-[28px] rounded-lg bg-slate-900 border border-white/10 hover:border-amber-500/50 flex items-center justify-center text-amber-400 transition-colors shadow-sm"
                  title="Tipps & Hilfe"
                >
                  <Lightbulb className="w-3.5 h-3.5" />
                </button>
              </div>
            );
          })()}
        </div>
      </div>
      {showHelp && <QuickTipsGallery onClose={() => setShowHelp(false)} />}
    </header >
  );
};

export default Header;
